import React, { useState, useMemo } from 'react';
import { Button, message, Table } from 'antd';
import type { TableColumnsType } from 'antd';
import AccountFormDrawer from './components/AccountFormDrawer';
import FilterArea, { DEFAULT_FILTER_OPTIONS } from './components/FilterArea';
import {
    GET_ACCOUNT_LISTING_FOR_TMS_WITH_PAGINATION,
    getAccountListingForTmsWithPaginationResponse,
    TmsAccountItemType,
} from './graphql/queries/getAccountListingForTms';
import { useQuery } from '@apollo/client';
import { getAccountTmsDriverByAccountId } from '../../../graphql/queries/getAccountTmsDriverByAccountId';
import { AccountFormData, useAccountApis } from 'hooks/useAccountApis';
import { RoleType } from 'graphql/types/AccountType';
import { FilterOptions } from './components/FilterArea';
import { useCurrentWarehouse } from 'store/warehouseStore';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import { showError } from 'utils/exceptionUtils';
import { useTranslation } from 'react-i18next';
import { GET_TMS_CARRIERS_BY_WAREHOUSE_ID } from '../../../graphql/queries/getTmsCarriers';
import { formatISODateTime } from '../../../utils/dateTimeUtils';
import TablePaginationBar from '../../../components/TablePaginationBar';

const TmsAccountPage: React.FC = () => {
    const { createAccount, updateAccount } = useAccountApis();
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState<AccountFormData | null>(null);
    const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
    const [filterOptions, setFilterOptions] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
    const [tempFilterOptions, setTempFilterOptions] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const warehouse = useCurrentWarehouse();
    const { currentWarehouse } = useWarehouseContext();

    const effectiveWarehouseId = currentWarehouse?.id ?? warehouse?.id;

    // 构建后端筛选参数
    const buildFilters = () => {
        const filters: Record<string, any> = {};
        if (effectiveWarehouseId) filters.warehouseId = Number(effectiveWarehouseId);
        if (filterOptions.id) {
            const idNum = Number(filterOptions.id);
            if (!isNaN(idNum)) filters.id = idNum;
        }
        if (filterOptions.name) filters.name = filterOptions.name;
        if (filterOptions.phone) filters.phone = filterOptions.phone;
        if (filterOptions.status !== 2) filters.status = filterOptions.status;
        if (filterOptions.carrierId !== null) filters.carrierId = filterOptions.carrierId;
        return Object.keys(filters).length > 0 ? filters : undefined;
    };

    const { data: paginatedData, loading, refetch } = useQuery<getAccountListingForTmsWithPaginationResponse>(
        GET_ACCOUNT_LISTING_FOR_TMS_WITH_PAGINATION,
        {
            variables: {
                filter: buildFilters(),
                pageRequest: {
                    page: currentPage,
                    size: pageSize,
                },
            },
            skip: !effectiveWarehouseId,
            fetchPolicy: 'no-cache',
        }
    );

    const listingResponse = paginatedData?.getAccountListingForTmsWithPagination;
    const accountList = listingResponse?.content ?? [];
    const totalElements = listingResponse?.totalElements ?? 0;

    // 获取承运商数据（用于承运商名称映射和筛选下拉框）
    const { data: carrierData } = useQuery(GET_TMS_CARRIERS_BY_WAREHOUSE_ID, {
        variables: { warehouseId: effectiveWarehouseId ? Number(effectiveWarehouseId) : null },
        skip: !effectiveWarehouseId
    });

    // 创建承运商选项和映射表
    const carrierOptions = useMemo(() => {
        const carriers = carrierData?.getTmsCarriersByWarehouseId;
        if (!carriers || carriers.length === 0) {
            return [];
        }
        return carriers.map((c: any) => ({
            label: c.carrierName,
            value: Number(c.id),
            status: c.status
        }));
    }, [carrierData]);

    const carrierMap = useMemo(() => {
        const carriers = carrierData?.getTmsCarriersByWarehouseId || [];
        const map = new Map<string, string>();
        carriers.forEach((c: any) => {
            map.set(c.id.toString(), c.carrierName);
        });
        return map;
    }, [carrierData]);

    // Show new account drawer
    const showNewAccountDrawer = () => {
        setSelectedAccount(null);
        setDrawerMode('create');
        setDrawerVisible(true);
    };
    const { t } = useTranslation();
    // Show account details
    const showAccountDetail = async (account: TmsAccountItemType) => {
        const formData: AccountFormData = {
            id: Number(account.id),
            phone: {
                countryCode: account.countryCode ?? 1,
                phoneNumber: account.phone ?? '',
            },
            name: account.name,
            email: account.email ?? '',
            role: account.role.name as RoleType,
            status: account.status,
            isOnDuty: true
        };
        const role = account.role.name;
        if (role === 'TMS_DRIVER') {
            const tmsDriver = await getAccountTmsDriverByAccountId(Number(account.id));
            formData.vehicleId = tmsDriver.vehicleId;
            formData.carrierId = tmsDriver.carrierId;
        } else {
            message.error(t('pickup.account.messages.unsupportedRole', {role: role}) || '');
            console.error(`Unsupported role: ${role}`);
            showError(t('pickup.account.messages.unsupportedRole', {role: role}) || '');
            return;
        }

        setSelectedAccount(formData);
        setDrawerMode('edit');
        setDrawerVisible(true);
    };

    // Handle account form submission (create or update)
    const handleAccountSubmit = async (values: AccountFormData) => {
        if (drawerMode === 'create') {
            try {
                console.log('New account submitted:', values);
                const result = await createAccount(values);
                if (!result) {
                    return;
                }
                setDrawerVisible(false);
                message.success(t('pickup.account.messages.personnelCreatedSuccessfully') || '');
                refetch();
            } catch (error) {
                console.error('Error creating account:', error);
                showError(error);
            }
        } else {
            try {
                console.log('Personnel updated:', values);
                const result = await updateAccount(values);
                if (!result) {
                    return;
                }
                setDrawerVisible(false);
                message.success(t('pickup.account.messages.personnelUpdatedSuccessfully') || '');
                refetch();
            } catch (error) {
                console.error('Error updating account:', error);
                showError(error);
            }
        }
    };

    // Table column definitions
    const columns: TableColumnsType<TmsAccountItemType> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            align: 'left' as const,
            ellipsis: false,
            render: (text, record) => (
                <Button
                    type="link"
                    onClick={() => showAccountDetail(record)}
                    style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 0, paddingBottom: 0, marginLeft: 0, marginRight: 0 }}
                    className="!p-0 !m-0"
                >
                    <span style={{ whiteSpace: 'nowrap' }}>{text}</span>
                </Button>
            ),
        },
        {
            title: t('pickup.account.columns.name'),
            dataIndex: 'name',
            key: 'name',
            align: 'left' as const,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('pickup.account.columns.phone'),
            dataIndex: 'phone',
            key: 'phone',
            align: 'left' as const,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('pickup.account.columns.role'),
            dataIndex: ['role', 'name'],
            key: 'role.name',
            align: 'left' as const,
            ellipsis: false,
            render: (_, record) => {
                const text = t(`enum.role.${record.role.name}`);
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            },
        },
        {
            title: t('tms.vehicle.columns.carrierId'),
            dataIndex: 'carrierId',
            key: 'carrierId',
            align: 'left' as const,
            ellipsis: false,
            render: (carrierId: number | null) => {
                const carrierName = carrierId ? carrierMap.get(carrierId.toString()) : undefined;
                return <span style={{ whiteSpace: 'nowrap' }}>{carrierName || '-'}</span>;
            }
        },
        {
            title: t('pickup.account.columns.status'),
            dataIndex: 'status',
            key: 'status',
            align: 'left' as const,
            ellipsis: false,
            render: (status: number) => {
                const color = status === 1 ? 'text-green-600' : 'text-gray-400';
                const text = status === 1 ? t('enum.activeStatus.active') : t('enum.activeStatus.suspended');
                return <span className={color} style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            },
        },
        {
            title: t('tms.account.columns.updatedAt'),
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            align: 'left' as const,
            ellipsis: false,
            render: (updatedAt: string) => {
                const text = formatISODateTime(updatedAt);
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            },
        },
    ];

    const handleFilter = () => {
        setFilterOptions(tempFilterOptions);
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setTempFilterOptions(DEFAULT_FILTER_OPTIONS);
        setFilterOptions(DEFAULT_FILTER_OPTIONS);
        setCurrentPage(1);
    };

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            {/* Filter area */}
            <FilterArea
                carrierOptions={carrierOptions}
                filters={tempFilterOptions}
                setFilters={setTempFilterOptions}
            />

            {/* 批量操作区域 */}
            <div className="flex justify-end items-center mb-3 pl-0">
                <div className="flex gap-2">
                    <Button
                        type="primary"
                        onClick={showNewAccountDrawer}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('pickup.account.buttons.new')}</span>
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleFilter}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('pickup.account.buttons.filter')}</span>
                    </Button>
                    <Button
                        onClick={handleResetFilters}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('pickup.account.buttons.reset')}</span>
                    </Button>
                </div>
            </div>

            {/* Personnel table */}
            <div className="[&_.ant-table-body]:overflow-scroll [&_.ant-table-body]:overflow-x-scroll [&_.ant-table-body]:overflow-y-scroll">
                <div className="[&_.ant-table-tbody>tr>td:first-child]:!pl-4 [&_.ant-table-thead>tr>th:first-child]:!pl-4">
                    <Table
                        rowKey="id"
                        loading={loading}
                        columns={columns}
                        dataSource={accountList}
                        pagination={false}
                        scroll={{x: 'max-content'}}
                        size="middle"
                        tableLayout="auto"
                     className="mb-4"/>
                </div>
            </div>

            {/* 左下角合计和分页 */}
            <TablePaginationBar
                total={totalElements}
                current={currentPage}
                pageSize={pageSize}
                onChange={(page, size) => {
                    setCurrentPage(page);
                    setPageSize(size || pageSize);
                }}
            />

            {/* Personnel form drawer (shared for create/edit) */}
            <AccountFormDrawer
                visible={drawerVisible}
                onClose={() => setDrawerVisible(false)}
                onSubmit={handleAccountSubmit}
                account={selectedAccount}
                mode={drawerMode}
                carrierOptions={carrierOptions}
            />
        </div>
    );
};

export default TmsAccountPage;